/**
 * @file PlayButton.jsx
 * @description A button component for triggering activation actions on elements, created for Story Items but designed to be reusable and customizable.
 * @requires React, PropTypes, semantic-ui-react, react-i18next
 * @exports PlayButton
 */

import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Popup } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';

import './ActionButtons.css';

/**
 * handleElementAction is a helper function to prevent redirection and perform the action
 *
 * @function handleElementAction
 * @param {React.SyntheticEvent} element - The event element to prevent redirection.
 * @param {Function} action - Function to perform the action (play).
 * @param {string} id - Element ID to be passed for the respective action.
 * @param {string} [saveId=null] - Save ID to be passed for the respective action. Optional.
 * @returns {Promise<void>} Resolves when the action is performed.
 *
 * @example
 * handleElementAction(eventElement, handlePlayEvent, eventElement.id, eventElement.idDoc);
 */
const handleElementAction = async (element, action, id, saveId = null) => {
    element.stopPropagation(); // Disables the redirection from the div zone
    try {
        await action(id, saveId);
    } catch (error) {
        console.error(`Failed to execute action: ${error.message}`);
    }
};

/**
 * @typedef {Object} PlayButtonProps
 * @property {Function} action - Callback function to handle the activation action. Should accept an `elementId`.
 * @property {string} elementId - Identifier of the element to be actived.
 * @property {string} [saveId=null] - Identifier of the save to be actived. Optional, defaults to `null`.
 * @property {string} [label='Play this element'] - Text label displayed on the button.
 * @property {string} [icon='play'] - Icon displayed on the button.
 * @property {string} [tooltipText] - Tooltip text displayed on hover. Defaults to localized "Play" text.
 * @property {string} [color='blue'] - Color theme of the button.
 */

/**
 * PlayButton is a reusable component specialized for activation tasks.
 *
 * @component
 * @param {PlayButtonProps} props - Component properties.
 * @returns {JSX.Element} A styled button component with an action and tooltip for activing elements.
 * @throws {Error} If required props `action` or `elementId` are missing.
 *
 * @example
 * const handlePlay = (id, saveId) => console.log(`Playing element with ID: ${id} saved as: ${saveId}`);
 * 
 * // Basic usage
 * <PlayButton
 *   action={handlePlay}
 *   elementId="12345"
 *   saveId="21598"
 * />
 *
 * // Customized button
 * <PlayButton
 *    action={handlePlay}
 *    elementId="12345"
 *    saveId="21598"
 *    label="Play"
 *    icon="play"
 *    tooltipText="Click to play"
 *    color="secondary"
 * />
 */
const PlayButton = ({ 
    action, 
    elementId,
    saveId = null,
    label = 'Play this element', 
    icon = 'play', 
    tooltipText, 
    color = 'blue'
}) => {
    if (!action) {
        throw new Error("PlayButton: Missing required prop 'action'. Expected a function.");
    }

    if (!elementId) {
        console.error("PlayButton: Missing required prop 'elementId'. Expected a string identifier. Received:", elementId);
        return null; // Do not render the button if elementId is missing
    }

    const { t } = useTranslation();

    const memoizedHandleElementAction = useCallback((element) => handleElementAction(element, action, elementId, saveId), [action, elementId, saveId]);

    const computedTooltipText = useMemo(() => tooltipText || t('Popup_Play'), [tooltipText, t]);
    
    return (
        <Popup
            trigger={
                <Button
                    className="element-buttons"
                    onClick={memoizedHandleElementAction}
                    icon
                    color={color}
                    aria-label={label}
                >
                    <Icon name={icon} />
                </Button>
            }
            content={computedTooltipText}
            on='hover'
        />
    );
};

PlayButton.propTypes = {
    action: PropTypes.func.isRequired,
    elementId: PropTypes.string.isRequired,
    saveId: PropTypes.string,
    label: PropTypes.string,
    icon: PropTypes.string,
    tooltipText: PropTypes.string,
    color: PropTypes.string
};

export default PlayButton;
